// Day-first (DD-MM-YYYY) date field for register forms.
// Type the date manually (DD-MM-YYYY / DD/MM/YYYY) OR pick from the calendar
// (calendar has year/month dropdowns for quickly reaching old dates).
// Value is stored as ISO "yyyy-mm-dd" (backend-compatible).
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays, X } from "lucide-react";

export function formatDMY(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return String(iso);
  return `${d}-${m}-${y}`;
}

export function parseDMY(text) {
  const t = String(text || "").trim();
  if (!t) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t; // ISO passthrough
  const m = t.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (!m) return null; // invalid format
  const [, d, mo, y] = m;
  const dt = new Date(`${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}T00:00:00`);
  if (isNaN(dt.getTime()) || dt.getDate() !== +d || dt.getMonth() + 1 !== +mo) return null;
  return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
}

function isoToDate(iso) {
  if (!iso) return undefined;
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
  return isNaN(d.getTime()) ? undefined : d;
}

export default function DatePicker({ value, onChange, placeholder = "DD-MM-YYYY", testid }) {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState(formatDMY(value));

  React.useEffect(() => { setText(formatDMY(value)); }, [value]);

  const commit = () => {
    const iso = parseDMY(text);
    if (iso === null) {
      setText(formatDMY(value)); // revert invalid input
      return;
    }
    if (iso !== (value || "")) onChange(iso);
    setText(formatDMY(iso));
  };

  return (
    <div className="flex gap-1.5">
      <Input
        value={text}
        placeholder={placeholder}
        onChange={e => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
        className="bg-white"
        data-testid={testid}
      />
      {value && (
        <Button type="button" variant="ghost" size="icon" className="shrink-0 h-10 w-9 text-muted-foreground"
          onClick={() => { onChange(""); setText(""); }}
          data-testid={testid ? `${testid}-clear` : undefined}>
          <X className="w-4 h-4" />
        </Button>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="icon" className="shrink-0 h-10 w-10" data-testid={testid ? `${testid}-cal` : undefined}>
            <CalendarDays className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            captionLayout="dropdown-buttons"
            fromYear={1980}
            toYear={new Date().getFullYear() + 5}
            selected={isoToDate(value)}
            onSelect={(d) => {
              if (d) {
                const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                onChange(iso);
              }
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
